/* The landing page's first screen: "Where do you want to go?" over a big night globe that turns slowly, with flights
   crossing it. A tap anywhere on it (or a search, or Enter on the globe) zooms in on the closest university and opens
   its breakdown: where it is, its world rank and its rank in its country, a year's cost, how selective it is, and what
   it's known for. Scrolling up or pinching over the globe zooms it (not the page). "Let's begin" dives into the globe
   and opens the account sign-up, on the same night screen; once the account is made, the planner opens with that
   university's town picked and the university on the list (/planner/#begin=<id>). A signed-in student sees the same
   page, and their "Let's begin" goes straight to their plan. "Try the demo" opens the planner's demo, a sample student
   kept apart from any account (/planner/?demo). The guide, a little 3D book (packages/core src/mascot), shows visitors
   around the page and reacts when they pick a university.
   The drawing is lib/globe-view.ts; the data (lib/globe-data.ts) loads after the page, from src/pages/landing/globe/
   (made by scripts/landing-globe.mjs from the planner's directory, so both show the same universities). */
import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { WEB_ROUTES } from '@pathabroad/core/browser';
import { useT } from '../../app/i18n';
import { Link, navigate } from '../../app/nav';
import { useSession } from '../../app/session';
import { mascot, type Guide } from '@pathabroad/core/mascot';
import { Icon } from '../../app/icons';
import { countryName, localeOf } from '../../lib/format';
import { GlobeView, type Layout } from '../../lib/globe-view';
import {
  beginHref, coords, isBand, nearest, plannerBegin, rankedCounts, search, selectivity, usdShort, type GlobeData, type Hit, type LL,
  type School,
} from '../../lib/globe-data';
import type { MessageKey } from '../../lib/i18n';
import { COUNTRIES, UNIVERSITIES } from './facts';

/* where flights leave from: cities many of the students come from */
const ORIGINS: LL[] = [
  [6.52, 3.38], [28.61, 77.21], [19.08, 72.88], [30.04, 31.24], [24.71, 46.68], [31.95, 35.93], [24.86, 67.01], [23.81, 90.41],
  [-1.29, 36.82], [5.6, -0.19], [14.6, 120.98], [-6.21, 106.85], [19.43, -99.13], [-23.55, -46.63], [4.71, -74.07], [41.01, 28.98],
  [27.72, 85.32], [6.93, 79.86], [9.03, 38.74], [33.57, -7.59], [21.03, 105.85], [39.9, 116.41], [-12.05, -77.04], [-26.2, 28.05],
  [35.69, 51.39], [33.31, 44.36], [41.3, 69.24], [12.97, 77.59], [17.39, 78.49], [36.81, 10.18],
];
/* where they land: the towns with the most universities */
function destinations(d: GlobeData): LL[] {
  const by = new Map<string, { n: number; lat: number; lon: number }>();
  for (const s of d.schools) {
    const k = s.c + '|' + (s.s ?? '') + '|' + s.ci, p = by.get(k) ?? { n: 0, lat: 0, lon: 0 };
    p.n++; p.lat += s.a[0]; p.lon += s.a[1]; by.set(k, p);
  }
  return [...by.values()].sort((a, b) => b.n - a.n).slice(0, 48).map(p => [p.lat / p.n, p.lon / p.n] as LL);
}

/* the planner's demo: a sample student, kept in that browser tab and never saved to an account */
export const DEMO_HREF = '/planner/?demo';

/* the planner's files, fetched once the visitor has picked somewhere, so it opens quickly after the sign-up */
let prefetched = false;
function prefetchPlanner(): void {
  if (prefetched) return;
  prefetched = true;
  if ((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData) return;
  const add = (href: string) => { const l = document.createElement('link'); l.rel = 'prefetch'; l.href = href; document.head.appendChild(l); };
  fetch(WEB_ROUTES.planner, { credentials: 'same-origin' }).then(r => (r.ok ? r.text() : ''))
    .then(html => {
      const bridge = html.match(/\/planner\/bridge\.[0-9a-f]+\.js/);
      if (!bridge) return;
      return fetch(bridge[0]).then(r => (r.ok ? r.text() : '')).then(js => { const app = js.match(/\/planner\/planner\.[0-9a-f]+\.js/); if (app) add(app[0]); });
    })
    .catch(() => {});
}

type Pick = { s: School; d: number | null };   // d: km from where the visitor tapped (null: they searched)
type Blurbs = { lang: string; map: Record<string, string> };

export function GlobeHero() {
  const { t, rich, lang } = useT();
  const { session } = useSession();
  /* a visitor makes an account first; a signed-in student goes straight to their plan with the university */
  const signedIn = session.status === 'in';
  const hrefFor = (s: School) => (signedIn ? plannerBegin(s) : beginHref(s));
  const heroRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hudRef = useRef<HTMLElement>(null);
  const viewRef = useRef<GlobeView | null>(null);
  const dataRef = useRef<GlobeData | null>(null);
  const [data, setData] = useState<GlobeData | null>(null);
  const [pick, setPick] = useState<Pick | null>(null);
  const [q, setQ] = useState('');
  const [leaving, setLeaving] = useState(false);
  const [blurbs, setBlurbs] = useState<Blurbs | null>(null);
  const leaveTimer = useRef<number | undefined>(undefined);
  const guide = useRef<Guide | null>(null);
  const rtl = lang === 'ar';
  const n = (v: number) => new Intl.NumberFormat(localeOf(lang)).format(v);
  const counts = useMemo(() => (data ? rankedCounts(data.schools) : {}), [data]);

  /* where the globe sits: beside the words on wide screens (beside the breakdown once there's a pick), below them on
     phones (above the breakdown) */
  const layoutFor = (picked: boolean): Layout => {
    const el = heroRef.current, w = el?.clientWidth ?? innerWidth, side = rtl ? -1 : 1;
    if (w >= 960) return picked ? { fit: 0.44, shiftX: -0.17 * side, shiftY: 0.02 } : { fit: 0.5, shiftX: 0.17 * side, shiftY: 0.05 };
    return picked ? { fit: 0.5, shiftX: 0, shiftY: -0.22 } : { fit: w < 600 ? 0.66 : 0.54, shiftX: 0, shiftY: 0.21 };
  };
  const latest = useRef({ pick, leaving, layoutFor, choose: (_s: School, _d: number | null) => {} });

  const loadBlurbs = (l: string) => {
    if (blurbs?.lang === l) return;
    const load = l === 'ar' ? import('./globe/blurbs-ar.json') : l === 'hi' ? import('./globe/blurbs-hi.json') : l === 'id' ? import('./globe/blurbs-id.json') : import('./globe/blurbs-en.json');
    load.then(m => setBlurbs({ lang: l, map: m.default as Record<string, string> })).catch(() => {});
  };
  const choose = (s: School, d: number | null) => {
    const v = viewRef.current;
    guide.current?.pick({ name: s.n });
    setPick({ s, d }); setQ('');
    if (v) { v.setTarget(s.a); v.spin(false); v.layout(layoutFor(true)); v.flyTo(s.a[0], s.a[1], 2.2, 1500); }
    prefetchPlanner();
    loadBlurbs(lang);
  };
  const close = () => {
    const v = viewRef.current;
    guide.current?.pick(null);
    setPick(null);
    if (v) { const now = v.view(); v.setTarget(null); v.setAnchor(null); v.layout(layoutFor(false)); v.flyTo(now.lat, now.lon, 1, 1100); v.spin(true); v.el.focus({ preventScroll: true }); }
  };
  latest.current = { pick, leaving, layoutFor, choose };

  /* the globe: one for the page's life; the data loads after the page */
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    let early: LL | null = null;   // a tap before the universities have loaded: answered when they arrive
    const pickNear = (d: GlobeData, ll: LL) => { const hit = nearest(d.schools, ll); if (hit) latest.current.choose(hit.s, hit.d); };
    const v = new GlobeView(cv, {
      label: '',
      onPick: ll => {
        const d = dataRef.current;
        if (!ll || latest.current.leaving) return;
        if (d) pickNear(d, ll); else early = ll;
      },
    });
    viewRef.current = v;
    v.layout(latest.current.layoutFor(false), true);
    const onResize = () => v.layout(latest.current.layoutFor(!!latest.current.pick), true);
    addEventListener('resize', onResize);
    let live = true;
    import('./globe/schools.json').then(m => {
      if (!live) return;
      const d = m.default as GlobeData;
      dataRef.current = d;
      v.setSchools(d.schools.map(s => s.a), d.schools.map(s => !!s.w));
      v.setFlights(ORIGINS, destinations(d));
      setData(d);
      if (early) { pickNear(d, early); early = null; }
    }).catch(() => {});
    return () => { live = false; removeEventListener('resize', onResize); v.destroy(); viewRef.current = null; };
  }, []);

  /* the globe's name and how to use it, in the page's language; the layout follows the reading direction */
  useEffect(() => {
    const v = viewRef.current;
    if (!v) return;
    v.el.setAttribute('aria-label', t('land.globe.aria', { unis: n(UNIVERSITIES) }));
    v.layout(layoutFor(!!pick), true);
    if (pick) loadBlurbs(lang);
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  /* the line from the reticle to the breakdown, and the breakdown's heading gets focus when it opens (and again when
     the visitor comes back from the planner) */
  useEffect(() => {
    const v = viewRef.current, cv = canvasRef.current, hud = hudRef.current;
    if (!v || !cv) return;
    if (!pick || !hud || leaving) { v.setAnchor(null); return; }
    const place = () => {
      const c = cv.getBoundingClientRect(), r = hud.getBoundingClientRect();
      v.setAnchor(c.width >= 960 ? { x: (rtl ? r.right : r.left) - c.left, y: r.top - c.top + 34 } : { x: r.left - c.left + r.width / 2, y: r.top - c.top });
    };
    place();
    const ro = 'ResizeObserver' in window ? new ResizeObserver(place) : null;
    ro?.observe(hud);
    addEventListener('resize', place);
    hud.querySelector<HTMLElement>('#lg-name')?.focus({ preventScroll: true });
    return () => { ro?.disconnect(); removeEventListener('resize', place); };
  }, [pick, rtl, leaving]);

  /* back from the planner: browsers can bring this page back exactly as it was left (mid-dive, under the night, with
     the timer that opens the planner still waiting if they left in the middle). Pull back out to the breakdown instead. */
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (!e.persisted || !latest.current.leaving) return;
      window.clearTimeout(leaveTimer.current);
      const v = viewRef.current, p = latest.current.pick;
      setLeaving(false);
      if (v) { v.flightsEnabled(true); if (p) v.flyTo(p.s.a[0], p.s.a[1], 2.2, 700); }
      guide.current?.hold(false);
    };
    addEventListener('pageshow', onShow);
    return () => removeEventListener('pageshow', onShow);
  }, []);

  /* the guide: he shows visitors around (for the visitors who get him, loaded after the page: see packages/core
     src/mascot), and leaves with the page */
  useEffect(() => {
    const g = mascot('landing', {}, () => import('@pathabroad/core/mascot/book3d'));
    guide.current = g;
    return () => { g?.destroy(); guide.current = null; };
  }, []);

  /* leaving the page some other way during the dive: the sign-up doesn't open by itself afterwards */
  useEffect(() => () => window.clearTimeout(leaveTimer.current), []);

  /* Escape closes the breakdown */
  useEffect(() => {
    if (!pick) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !latest.current.leaving) close(); };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [pick]); // eslint-disable-line react-hooks/exhaustive-deps

  const hits: Hit[] = useMemo(() => (data ? search(data.schools, q, c => [countryName(c, 'en'), countryName(c, 'ar'), countryName(c, 'hi'), countryName(c, 'id')]) : []), [data, q]);

  /* "Let's begin": the breakdown folds away, the globe dives into the university, the night closes in from it, and the
     sign-up opens on the same night screen (then the planner, with the university); a signed-in student's plan opens */
  const begin = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    if (!pick || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    const href = hrefFor(pick.s), v = viewRef.current, hero = heroRef.current;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !v || !hero) { navigate(href); return; }
    const at = v.project(pick.s.a[0], pick.s.a[1]);
    hero.style.setProperty('--wx', `${Math.round(at.x)}px`);
    hero.style.setProperty('--wy', `${Math.round(at.y)}px`);
    setLeaving(true);
    guide.current?.hold();
    v.flightsEnabled(false); v.setAnchor(null);
    v.flyTo(pick.s.a[0], pick.s.a[1], 4.4, 950);
    leaveTimer.current = window.setTimeout(() => navigate(href), 880);
  };

  return (
    <section ref={heroRef} className={`land-hero lg-hero night${pick ? ' has-pick' : ''}${leaving ? ' is-leaving' : ''}`} aria-labelledby="page-title">
      <canvas ref={canvasRef} className="lg-canvas" />
      <div className="lg-shade" aria-hidden="true" />
      <div className="land-wrap lg-copy">
        <p className="lg-eyebrow"><Icon.globe />{t('land.facts.unis', { unis: n(UNIVERSITIES), countries: n(COUNTRIES) })}</p>
        <h1 id="page-title" className="land-display lg-title" tabIndex={-1}>{t('land.globe.title')}</h1>
        <p className="lg-lead">{t('land.globe.lead')}</p>
        <div className="lg-find">
          <label className="lg-search">
            <Icon.search />
            <span className="sr-only">{t('land.globe.search')}</span>
            <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder={t('land.globe.search')}
              autoComplete="off" enterKeyHint="search" aria-controls="lg-results"
              onKeyDown={e => { if (e.key === 'Enter' && hits[0]) { e.preventDefault(); choose(hits[0].s, null); } }} />
          </label>
          <div id="lg-results" className="lg-results" aria-live="polite">
            {q.trim().length >= 2 && data && (hits.length
              ? <ul>{hits.map(h => (
                  <li key={h.kind + h.s.i + h.label}>
                    <button type="button" onClick={() => choose(h.s, null)}>
                      <b>{h.kind === 'country' ? countryName(h.label, lang) : h.kind === 'city' ? (lang === 'ar' && h.s.ca ? h.s.ca : h.label) : h.s.n}</b>
                      <span>{h.kind === 'uni' ? placeOf(h.s, lang) : h.s.n}</span>
                    </button>
                  </li>))}
                </ul>
              : <p>{t('land.globe.none')}</p>)}
          </div>
        </div>
        <p className="lg-more">
          <a href={WEB_ROUTES.planner} className="lg-skip">{t(signedIn ? 'home.planner' : 'home.start')}<Icon.arrow /></a>
          <a href={DEMO_HREF} className="lg-demo"><Icon.play />{t('land.globe.demo')}</a>
          <span>{t('land.hero.note')}</span>
        </p>
        {!signedIn && <p className="lg-signin">{rich('land.hero.signin', { link: <Link to={WEB_ROUTES.signin}>{t('home.signin')}</Link> })}</p>}
      </div>
      {pick && (
        <section ref={hudRef} className="lg-hud" aria-labelledby="lg-name" key={pick.s.i}>
          <div className="lg-hud__scan" aria-hidden="true" />
          <header className="lg-hud__head">
            <p className="lg-hud__eye">
              <Icon.target />
              <span>{pick.d == null ? t('land.globe.yourPick') : t('land.globe.closest')}</span>
              {pick.d != null && <span className="lg-hud__away">{pick.d < 3 ? t('land.globe.here') : t('land.globe.away', { d: t('land.globe.km', { n: n(Math.round(pick.d)) }) })}</span>}
            </p>
            <button type="button" className="lg-hud__x" onClick={close} aria-label={t('land.globe.close')}><Icon.close /></button>
          </header>
          <h2 id="lg-name" className="lg-hud__name" tabIndex={-1}>{pick.s.n}</h2>
          <p className="lg-hud__place">{placeOf(pick.s, lang)}<span className="lg-hud__ll" dir="ltr">{coords(pick.s.a)}</span></p>
          <dl className="lg-hud__stats">
            {stats(pick.s, counts, lang, t, n).map(([label, value, note], i) => (
              <div key={i} className="lg-stat" style={{ animationDelay: `${120 + i * 70}ms` }}>
                <dt>{label}</dt>
                <dd><b dir={/[\u0600-\u06FF\u0900-\u097F]/.test(value) ? undefined : 'ltr'}>{value}</b>{note && <span>{note}</span>}</dd>
              </div>
            ))}
          </dl>
          <ul className="lg-hud__tags">
            {tags(pick.s, data?.langs[pick.s.c] ?? [], lang, t, n).map(x => <li key={x}>{x}</li>)}
          </ul>
          {blurbs?.lang === lang && blurbs.map[pick.s.i] && <p className="lg-hud__about">{blurbs.map[pick.s.i]}</p>}
          <div className="lg-hud__go">
            <a className="btn btn--primary lg-begin" href={hrefFor(pick.s)} onClick={begin}>{t('land.globe.begin')}<Icon.arrow /></a>
            <button type="button" className="lg-again" onClick={close}>{t('land.globe.again')}</button>
          </div>
        </section>
      )}
      <div className="lg-warp" aria-hidden="true" />
      <a className="lg-down" href="#how">{t('land.nav.how')}<Icon.down /></a>
    </section>
  );
}

/* "Toronto, Canada" (in Arabic, the Arabic name of the town); US universities give the state */
function placeOf(s: School, lang: string): string {
  const city = lang === 'ar' && s.ca ? s.ca : s.ci;
  return s.c === 'US' && s.s ? `${city}, ${s.s}` : `${city}${lang === 'ar' ? '، ' : ', '}${countryName(s.c, lang as 'en')}`;
}

type T = (key: MessageKey, vars?: Record<string, string>) => string;
/* the four numbers: world rank, rank in the country, a year's cost, and the acceptance rate (or how selective it is) */
function stats(s: School, counts: Record<string, number>, lang: string, t: T, n: (v: number) => string): Array<[string, string, string]> {
  const country = countryName(s.c, lang as 'en');
  const world = s.w ? (isBand(s.w) ? s.w.replace('-', '–') : '#' + s.w.replace(/^=/, '')) : t('land.globe.notRanked');
  const out: Array<[string, string, string]> = [
    [t('land.globe.rank'), world, s.w ? 'QS 2027' : ''],
    [t('land.globe.inCountry', { country }), s.r ? t('land.globe.rankOf', { r: n(s.r), n: n(counts[s.c] ?? s.r) }) : '—', ''],
    [t('land.globe.cost'), '≈ ' + usdShort(s.y), s.k ? (s.ty ? t('land.globe.costOwn', { year: s.ty }) : t('land.globe.costOwn0')) : t('land.globe.costEst')],
  ];
  const sel = selectivity(s);
  const year = (s.my ?? '').match(/\d{4}(?:[/–-]\d{2,4})?/);
  if (typeof s.m === 'number') out.push([t('land.globe.admit'), n(s.m) + '%', year ? t('land.globe.admitYear', { year: year[0] }) : '']);
  else out.push([t('land.globe.select'), sel ? t(`land.globe.sel.${sel}` as MessageKey) : '—', sel && !s.cc ? t('land.globe.selRank') : '']);
  return out;
}
/* public or private, the teaching languages, students, founded */
function tags(s: School, langs: string[], lang: string, t: T, n: (v: number) => string): string[] {
  let names: string[] = [];
  try { const dn = new Intl.DisplayNames([localeOf(lang as 'en')], { type: 'language' }); names = langs.map(l => dn.of(l) ?? l); } catch { names = langs; }
  const out = [s.cc ? t('land.globe.cc') : s.p ? t('land.globe.public') : t('land.globe.private')];
  if (names.length) out.push(t('land.globe.taught', { country: countryName(s.c, lang as 'en'), langs: names.join(lang === 'ar' ? '، ' : ', ') }));
  if (s.z) out.push(t('land.globe.students', { n: n(s.z) }));
  if (s.f) out.push(t('land.globe.founded', { year: String(s.f) }));
  return out;
}
